import React, { useMemo, useRef } from 'react';
import { arrayOf, bool, func, node, object, oneOf, shape, string } from 'prop-types';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { IconSpinner, ValidationError } from '..';
import css from './FieldAddress.module.css';
import {
  countryPostalCode,
  defaultPostalCodeRegex,
  defaultPostalCodeRegexStr,
  languageSelectOptions,
} from '../../marketplace-custom-config';
import { useState } from 'react';
import { oneOfType } from 'prop-types';
import { ReactComponent as ArrowDown } from '../../assets/svg/arrowDown.svg';
import { ReactComponent as ArrowUp } from '../../assets/svg/arrowUp.svg';
import { injectIntl } from 'react-intl';
import { CustomGeocoderMapbox } from '../LocationAutocompleteInput/GeocoderMapbox';

const langOptions = Object.keys(languageSelectOptions);
const searchTimeout = 500;
const minSearchLength = 3;

const addAddress = (values, name, value) => {
  let parentObject = values;
  if (typeof parentObject !== 'object' || parentObject.constructor !== Object) {
    parentObject = {};
  }
  parentObject = { ...parentObject };
  const keys = name.split('.').slice(1);
  const finalKey = keys.pop();
  keys.reduce((obj, key) => (obj[key] = obj[key] || {}), parentObject)[finalKey] = value;
  return parentObject;
};

const FieldAddressComponent = props => {
  const {
    rootClassName,
    className,
    errorClassName,
    id,
    addressLabel,
    postalLabel,
    input,
    meta,
    onChange,
    addressPlaceholder,
    postalPlaceholder,
    intl,
    userNativeLang,
    disabled,
    fromAdminStaffPage,
    ...rest
  } = props;
  const geocoderInstance = useRef(new CustomGeocoderMapbox());
  const timeoutRef = useRef(null);
  const [predictions, setPredictions] = useState([]);
  const [predictionStatus, setPredictionStatus] = useState({
    isLoading: false,
    error: false,
    touched: false,
  });
  const [showPredictions, setShowPredictions] = useState(false);
  const [selectedIndx, setSelectedIndx] = useState(-1);
  const [isSearchTextFocused, setIsSearchTextFocused] = useState(false);

  if ((addressLabel || postalLabel) && !id) {
    throw new Error('id required when a label is given');
  }
  const addressId = `${id}.address`;
  const postalId = `${id}.postalCode`;

  const { invalid, touched, error } = meta;

  // Error message and input error styles are only shown if the
  // field has been touched and the validation has failed.
  const hasError = touched && invalid && error;

  // const selectClasses = classNames(selectClassName, css.select, {
  //   [css.selectSuccess]: input.value && valid,
  //   [css.selectError]: hasError,
  // });

  const { onChange: inputOnChange, value: inputValue, onBlur, name } = input;

  const handleOnBlur = event => {
    setIsSearchTextFocused(false);
    if (event.relatedTarget) return;
    event.preventDefault();
    event.stopPropagation();
    onBlur(inputValue);
    setShowPredictions(false);
  };

  const handlePropsOnChange = newValue => {
    if (typeof onChange === 'function') {
      onChange(newValue);
    } else {
      inputOnChange(newValue);
    }
  };

  const handleOnChange = event => {
    event.preventDefault();
    event.stopPropagation();
    const name = event.target.name;
    const value = event.target.value;
    if (name.includes('address')) {
      if (value.length >= minSearchLength) {
        setPredictionStatus(preStatus => ({ ...preStatus, isLoading: true, error: false }));
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => getPrediction(value), searchTimeout);
      } else {
        setPredictions([]);
      }
      const { postalCode } = inputValue || {};
      const newValue = addAddress(
        !!postalCode && value.length >= minSearchLength ? { postalCode } : {},
        name,
        value
      );
      handlePropsOnChange(newValue);
    } else {
      const newValue = addAddress(inputValue, name, value);
      handlePropsOnChange(newValue);
    }
  };

  const handleOnFocus = event => {
    event.preventDefault();
    event.stopPropagation();
    const value = event.target.value;
    if (value.length >= minSearchLength && !predictions.length && !predictionStatus.isLoading) {
      getPrediction(value);
    }
    setIsSearchTextFocused(true);
    setShowPredictions(true);
  };

  const handlenoOptionClick = event => {
    event.preventDefault();
    event.stopPropagation();
  };

  const getPrediction = searchQuery => {
    setPredictionStatus({ isLoading: true, error: false, touched: true });
    setSelectedIndx(-1);
    geocoderInstance.current
      .getPlacePredictions(searchQuery, langOptions)
      .then(resp => {
        const { predictions } = resp;
        const filteredPredictions = predictions.filter(prediction => !!prediction.context);
        const formattedPredictions = filteredPredictions.map(prediction =>
          geocoderInstance.current.getPlaceDetails(prediction, langOptions)
        );
        setPredictionStatus({ isLoading: false, error: false, touched: true });
        setPredictions(formattedPredictions);
      })
      .catch(err => {
        setPredictionStatus({ isLoading: false, error: true, touched: true });
      });
  };

  const handleOptionClick = (prediction, indx, event) => {
    handlenoOptionClick(event);
    setPredictionStatus(preStatus => ({ ...preStatus, isLoading: true, error: false }));
    const { origin, address, shortAddress } = prediction;
    geocoderInstance.current
      .getCountryByLatLng(origin.lat, origin.lng)
      .then(response => {
        const { name, iso2 } = response;
        const selectedCountry = countryPostalCode.find(
          country => country.iso2 === iso2 || country.name.toLowerCase() === name.toLowerCase()
        );
        const postalCodeRegex = new RegExp(
          selectedCountry?.postalCodeRegexStr || defaultPostalCodeRegexStr
        );
        const { default: defaultAddress } = address;

        let postalCode = defaultAddress.match(postalCodeRegex);
        postalCode = !!postalCode && Array.isArray(postalCode) ? postalCode[0] : '';
        const finalAddress = Object.entries(address).reduce((pre, [key, value]) => {
          pre[key] = value.replace(postalCode, '').replace(/\s,/g, ',');
          return pre;
        }, {});
        const { default: addrss, ...rest } = finalAddress;
        handlePropsOnChange({ address: addrss, postalCode, ...rest, origin, shortAddress });
        setPredictionStatus(preStatus => ({ ...preStatus, isLoading: false }));
        setShowPredictions(false);
        setSelectedIndx(indx);
      })
      .catch(err => {
        setPredictionStatus(preStatus => ({ ...preStatus, isLoading: false, error: true }));
      });
  };

  const {
    address = '',
    [languageSelectOptions.fr.value]: frAddress = '',
    [languageSelectOptions.en.value]: enAddress = '',
    postalCode = '',
  } = inputValue || {};

  const formattedAddress = isSearchTextFocused
    ? address
    : (userNativeLang === languageSelectOptions.en.value ? enAddress : frAddress) || address;

  const classes = classNames(rootClassName || css.root, className, {
    [errorClassName]: !!errorClassName && hasError,
  });

  const inputClasses = classNames(className, {
    [errorClassName]: !!errorClassName && hasError,
  });
  const businessInputClasses = classNames(css.addressDiv, className, {
    [errorClassName]: !!errorClassName && hasError,
  });

  return (
    <div className={classes} tabIndex="0" onBlur={handleOnBlur}>
      <div className={css.addressWrapper}>
        <div className={businessInputClasses}>
          {addressLabel ? <label htmlFor={addressId}>{addressLabel}</label> : null}
          <input
            id={addressId}
            placeholder={addressPlaceholder}
            name={`${name}.address`}
            onChange={handleOnChange}
            value={formattedAddress}
            onFocus={handleOnFocus}
            disabled={disabled}
          />
          {showPredictions && (
            <div className={css.optionsContainer}>
              <ul className={css.optionsWrapper}>
                {(address.length < minSearchLength ||
                  (predictionStatus.isLoading && !predictions.length)) && (
                  <li className={css.noSearchOption}>
                    <button
                      type="button"
                      onClick={handlenoOptionClick}
                      className={classNames({
                        [css.loadingResultBtn]: address.length >= minSearchLength,
                      })}
                    >
                      {address.length < minSearchLength ? (
                        <span>
                          {intl.formatMessage(
                            { id: 'FieldAddress.minResultReq' },
                            { min: minSearchLength }
                          )}
                        </span>
                      ) : (
                        <React.Fragment>
                          {' '}
                          <IconSpinner className={css.icon} />
                          <span>{intl.formatMessage({ id: 'FieldAddress.loadingResult' })}</span>
                        </React.Fragment>
                      )}
                    </button>
                  </li>
                )}
                {predictionStatus.error && (
                  <li>
                    <button
                      type="button"
                      onClick={handlenoOptionClick}
                      className={classNames({
                        [css.loadingResultBtn]: address.length >= minSearchLength,
                      })}
                    >
                      <span>{intl.formatMessage({ id: 'FieldAddress.failedResult' })}</span>
                    </button>
                  </li>
                )}
                {predictionStatus.touched &&
                  formattedAddress.length >= minSearchLength &&
                  !predictionStatus.isLoading &&
                  !predictions.length && (
                    <li className={css.noSearchOption}>
                      <button
                        type="button"
                        onClick={handlenoOptionClick}
                        className={classNames({
                          [css.loadingResultBtn]: address.length >= minSearchLength,
                        })}
                      >
                        <span>{intl.formatMessage({ id: 'FieldAddress.noResult' })}</span>
                      </button>
                    </li>
                  )}
                {predictionStatus.touched &&
                  predictions.length > 0 &&
                  predictions.map((prediction, indx) => {
                    return (
                      <li
                        key={indx}
                        className={classNames({
                          [css.selectedOption]: selectedIndx === indx,
                        })}
                      >
                        <button
                          type="button"
                          onClick={handleOptionClick.bind(null, prediction, indx)}
                          className={classNames({
                            [css.loadedResultLoading]: predictionStatus.isLoading,
                          })}
                        >
                          <span>
                            {langOptions.includes(userNativeLang)
                              ? prediction.address[userNativeLang]
                              : prediction.address.default}
                          </span>
                        </button>
                      </li>
                    );
                  })}
              </ul>
            </div>
          )}
        </div>
        {fromAdminStaffPage ? null : (
          <div className={inputClasses}>
            {postalLabel ? <label htmlFor={postalId}>{postalLabel}</label> : null}
            <input
              id={postalId}
              placeholder={postalPlaceholder}
              name={`${name}.postalCode`}
              onChange={handleOnChange}
              value={postalCode}
              disabled={disabled}
            />
          </div>
        )}
      </div>
      <ValidationError fieldMeta={meta} />
    </div>
  );
};

FieldAddressComponent.defaultProps = {
  rootClassName: null,
  className: null,
  errorClassName: null,
  id: null,
  addressLabel: null,
  postalLabel: null,

  userNativeLang: languageSelectOptions.en.value,
};

FieldAddressComponent.propTypes = {
  rootClassName: string,
  className: string,
  errorClassName: string,

  onChange: func,

  // Label is optional, but if it is given, an id is also required so
  // the label can reference the input in the `for` attribute
  id: string,
  addressLabel: string,
  postalLabel: string,

  // Generated by final-form's Field component
  input: object.isRequired,
  meta: object.isRequired,
  userNativeLang: oneOf(langOptions).isRequired,
};

const FieldAddressComp = injectIntl(FieldAddressComponent);

const FieldAddress = props => {
  return <Field component={FieldAddressComp} {...props} />;
};

export default FieldAddress;
